import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '../ui/sidebar';

/**
 * Collapsible sidebar group.
 *
 * One parent row (icon + label + subtle right chevron). Click expands inline
 * to reveal child items beneath, indented to align under the parent label.
 * Auto-expands on first mount when the current route matches a child, so a
 * deep link doesn't land on a collapsed group.
 *
 * Children render as their own SidebarMenuButton rows (same look as flat
 * top-level items in NavMain) just with extra left padding for the indent.
 * That keeps the visual language consistent with the rest of the sidebar —
 * no special "sub-button" styling, no left rule.
 */
export function NavCollapsible({
    title,
    icon: Icon,
    items = [],
    defaultOpen,
    open: controlledOpen,
    onOpenChange,
}) {
    const location = useLocation();
    const activePage = location.pathname;
    const { isMobile, setOpenMobile } = useSidebar();

    // Longest-prefix-match so a sub-route doesn't highlight every ancestor.
    const activeChildUrl = items.reduce((bestUrl, item) => {
        const url = item.url;
        if (!url) return bestUrl;
        const matches =
            activePage === url ||
            (url !== '/' && activePage.startsWith(url.endsWith('/') ? url : `${url}/`));
        if (!matches) return bestUrl;
        if (bestUrl === null || url.length > bestUrl.length) return url;
        return bestUrl;
    }, null);

    const hasActiveChild = activeChildUrl !== null;
    const [localOpen, setLocalOpen] = useState(() =>
        typeof defaultOpen === 'boolean' ? defaultOpen : hasActiveChild
    );
    const isControlled = typeof controlledOpen === 'boolean';
    const open = isControlled ? controlledOpen : localOpen;

    // Auto-expand when the user navigates into one of this group's children.
    useEffect(() => {
        if (!hasActiveChild) return;
        if (isControlled) {
            onOpenChange?.(true);
            return;
        }
        setLocalOpen(true);
    }, [hasActiveChild, isControlled, onOpenChange]);

    const handleMobileNavClose = () => {
        if (isMobile) setOpenMobile(false);
    };

    return (
        <SidebarGroup>
            <SidebarGroupContent className="tw-flex tw-flex-col tw-gap-2">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            type="button"
                            onClick={() => {
                                const nextOpen = !open;
                                if (isControlled) {
                                    onOpenChange?.(nextOpen);
                                } else {
                                    setLocalOpen(nextOpen);
                                }
                            }}
                            tooltip={title}
                            aria-expanded={open}
                            className={
                                // Parent row picks up a faint dot indicator
                                // when collapsed but containing the active
                                // route, so the user can see "I'm in here".
                                hasActiveChild && !open
                                    ? '!tw-font-medium tw-text-primary-theme'
                                    : ''
                            }
                        >
                            <>
                                {Icon && <Icon />}
                                <span className="tw-flex-1 tw-truncate">{title}</span>
                                <ChevronRight
                                    className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-muted-foreground tw-opacity-60 tw-transition-transform tw-duration-150 ${
                                        open ? 'tw-rotate-90' : ''
                                    }`}
                                />
                            </>
                        </SidebarMenuButton>
                    </SidebarMenuItem>

                    {open &&
                        items.map((item) => {
                            const isActive = activeChildUrl === item.url;
                            const ChildIcon = item.icon;
                            return (
                                <SidebarMenuItem key={item.title}>
                                    <Link
                                        to={item.url}
                                        onClick={handleMobileNavClose}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <SidebarMenuButton
                                            tooltip={item.title}
                                            // Indented to align children under
                                            // the parent label. No left rule;
                                            // we rely on the indent to signal
                                            // nesting.
                                            className={
                                                isActive
                                                    ? 'tw-min-w-8 tw-pl-7 tw-bg-primary-theme tw-text-primary-theme-foreground tw-duration-200 tw-ease-linear hover:!tw-bg-primary-theme/90 hover:!tw-text-primary-theme-foreground active:tw-bg-primary-theme/90 active:tw-text-primary-theme-foreground'
                                                    : 'tw-pl-7'
                                            }
                                        >
                                            <>
                                                {ChildIcon && <ChildIcon />}
                                                <span className="tw-flex-1 tw-truncate">
                                                    {item.title}
                                                </span>
                                                {item?.badge ? (
                                                    <Badge
                                                        variant="secondary"
                                                        className="tw-ml-auto !tw-rounded-full tw-text-xs"
                                                    >
                                                        {item.badge}
                                                    </Badge>
                                                ) : null}
                                            </>
                                        </SidebarMenuButton>
                                    </Link>
                                </SidebarMenuItem>
                            );
                        })}
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
}
